"use client";

/*
 * PackTour — a guided, step-by-step tour of the RFP Pack flow for end users /
 * laypeople. It ships two tours depending on where you start:
 *
 *   "create" — from the packs home, through the wizard, ending at the
 *             "Generate comprehensive RFP pack" button.
 *   "canvas" — on a generated pack page: status, outline, gaps, download,
 *              share (the options that page offers).
 *
 * Each step can spotlight an element via `target`, or move to another page via
 * `page` / `nextRoute`. The tour persists in localStorage so it survives page
 * navigation and can be resumed.
 */

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";

const LS_KEY = "rfp-pack-tour-v2";
const LS_VISITED = "rfp-pack-tour-seen";

const DASH = "/agentic-workflows/rfp-packs";
const NEW = "/agentic-workflows/rfp-packs/new";
const PACK_RE = /^\/agentic-workflows\/rfp-packs\/(?:shared\/)?[^/]+$/;

type TourId = "create" | "canvas";

interface TourStep {
  key: string;
  title: string;
  body: string;
  target?: string;
  page?: string;
  nextRoute?: string;
  placement?: "bottom" | "top";
}

const CREATE_TOUR: TourStep[] = [
  {
    key: "welcome",
    title: "Welcome to RFP Packs",
    body: "This is your home for every pack you build. It shows how many packs you have, whether each is ready to send out or still a draft, and any gaps still to sort out.",
    page: DASH,
  },
  {
    key: "new-pack",
    title: "Start a new pack",
    body: "Click '+ New pack' to kick one off — or open an existing pack from the list below and carry on from there.",
    page: DASH,
    target: "[data-tour=new-pack]",
    placement: "bottom",
    nextRoute: NEW,
  },
  {
    key: "category",
    title: "Pick a category",
    body: "Choose what you're buying, e.g. Digital Marketing or IT Managed Services. This picks the right starting template, so the pack talks about the right things.",
    page: NEW,
    target: "[data-tour=pk-category]",
    placement: "bottom",
  },
  {
    key: "why",
    title: "Why are you running this?",
    body: "Tell Sensei the reason — a contract expiring, volumes up, a partner struggling. It uses this to write the purpose and scope of the pack.",
    page: NEW,
    target: "[data-tour=pk-why]",
    placement: "bottom",
  },
  {
    key: "timeline",
    title: "Set the procurement timeline",
    body: "These are required — the day the pack goes out, the day bidders must reply, and the time on that day. They appear on the cover and everywhere else, so everyone sees the same clock.",
    page: NEW,
    target: "[data-tour=pk-timeline]",
    placement: "bottom",
  },
  {
    key: "vision",
    title: "What good looks like",
    body: "In plain words, what success looks like — for example '98% on-time deliveries'. Focus on the outcome, and Sensei writes the document around it.",
    page: NEW,
    target: "[data-tour=pk-vision]",
    placement: "bottom",
  },
  {
    key: "constraints",
    title: "Constraints",
    body: "Anything that must be true — a go-live date, a fixed term, a system you must keep. These become hard requirements bidders have to meet.",
    page: NEW,
    target: "[data-tour=pk-constraints]",
    placement: "bottom",
  },
  {
    key: "essential",
    title: "Anything we must not lose?",
    body: "The things that can never change, like keeping your existing ERP integration. Sensei protects them in the compliance matrix and the terms.",
    page: NEW,
    target: "[data-tour=pk-essential]",
    placement: "bottom",
  },
  {
    key: "context",
    title: "Optional context",
    body: "The only optional box on the form — client name, currency or notes for the pack. Leave it blank and Sensei will use sensible defaults.",
    page: NEW,
    target: "[data-tour=pk-context]",
    placement: "top",
  },
  {
    key: "generate",
    title: "You're all set — generate your pack",
    body: "When your answers look right, click 'Generate comprehensive RFP pack'. Sensei builds all 14 sections, plus the scorecard, matrix and templates, from what you wrote. The tour ends here — after generating, look for the pack-page tour to explore your new pack.",
    page: NEW,
    target: "[data-tour=pk-generate]",
    placement: "bottom",
  },
];

const CANVAS_TOUR: TourStep[] = [
  {
    key: "pack-status",
    title: "Meet your pack",
    body: "This is the document Sensei just built for you. Check the issue date, response deadline and category first — then work through the sections. Ready means it can go out; draft means there's context left to fill.",
    target: "[data-tour=pk-status]",
    placement: "bottom",
  },
  {
    key: "outline",
    title: "The document outline",
    body: "Every section of the pack lives here, top to bottom: purpose, scope, process, compliance, pricing, terms and more. Click any section to jump straight to it.",
    target: "[data-tour=pk-outline]",
    placement: "bottom",
  },
  {
    key: "edit-delete",
    title: "Edit or remove a section",
    body: "Each section has two quick actions: the pencil opens it for editing, and the bin hides it from the pack. Changes are saved instantly and appear in the download — removed sections can be restored under 'Removed sections'.",
    target: "[data-tour=pk-edit]",
    placement: "bottom",
  },
  {
    key: "gaps",
    title: "Gaps & assumptions",
    body: "Sensei keeps a checklist of anything still missing. Fill them here to complete the pack — or leave them as [TBD] when they don't apply.",
    target: "[data-tour=pk-gaps]",
    placement: "bottom",
  },
  {
    key: "download",
    title: "Download the pack",
    body: "When you're happy, click 'Download draft pack' — you get a single ZIP with the main document, matrix, scorecard, templates and a user guide PDF to hand to your team.",
    target: "[data-tour=pk-download]",
    placement: "top",
  },
  {
    key: "share",
    title: "Share with your team",
    body: "Use 'Share' to send a read-only copy to colleagues for review — everyone can comment without touching the master pack.",
    target: "[data-tour=pk-share]",
    placement: "bottom",
  },
];

const ACCENT = "#5B6CFF";
const ACCENT2 = "#EC4899";
const CARD_FONT = "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

interface StoredTour { t: TourId; s: number; }

interface PackTourApi {
  active: boolean;
  tour: TourId;
  step: number;
  total: number;
  start: (it?: TourId) => void;
  finish: () => void;
}

const TourCtx = createContext<PackTourApi | null>(null);

export function usePackTour(): PackTourApi | null {
  return useContext(TourCtx);
}

function stepsFor(t: TourId): TourStep[] {
  return t === "canvas" ? CANVAS_TOUR : CREATE_TOUR;
}

function readStored(): StoredTour | null {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredTour;
    if ((parsed.t === "create" || parsed.t === "canvas") && Number.isInteger(parsed.s) && parsed.s >= 0 && parsed.s < stepsFor(parsed.t).length) return parsed;
  } catch { /* ignore corrupt storage */ }
  return null;
}

function writeStored(it: StoredTour | null) {
  try {
    if (it) localStorage.setItem(LS_KEY, JSON.stringify(it));
    else localStorage.removeItem(LS_KEY);
  } catch { /* private mode — tour still works, just doesn't resume */ }
}

export default function PackTour({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname() ?? "";
  const [tour, setTour] = useState<TourId | null>(null);
  const [step, setStep] = useState(-1); // -1 = inactive

  const tourRef = useRef(tour);
  const stepRef = useRef(step);
  const pathRef = useRef(pathname);
  useEffect(() => { tourRef.current = tour; }, [tour]);
  useEffect(() => { stepRef.current = step; }, [step]);
  useEffect(() => { pathRef.current = pathname; }, [pathname]);

  // Re-arm a stored tour after first paint (avoids SSR hydration mismatch).
  useEffect(() => {
    const stored = readStored();
    if (!stored) return;
    const id = requestAnimationFrame(() => {
      setTour(stored.t);
      setStep(stored.s);
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const commit = useCallback((next: TourId | null, s: number) => {
    setTour(next);
    setStep(s);
    writeStored(next ? { t: next, s } : null);
  }, []);

  const finish = useCallback(() => commit(null, -1), [commit]);

  const start = useCallback((it?: TourId) => {
    const chosen: TourId = it ?? (PACK_RE.test(pathRef.current) ? "canvas" : "create");
    commit(chosen, 0);
    const def = stepsFor(chosen)[0];
    if (def.page && pathRef.current !== def.page) router.push(def.page);
  }, [commit, router]);

  // Navigate to each step's home page when the tour is active there.
  useEffect(() => {
    if (step < 0 || !tour) return;
    const def = stepsFor(tour)[step];
    if (def.page && pathname !== def.page) router.push(def.page);
  }, [step, tour, pathname, router]);

  const next = useCallback(() => {
    if (step < 0 || !tour) return;
    const list = stepsFor(tour);
    if (step >= list.length - 1) { finish(); return; }
    const def = list[step];
    if (def.nextRoute) router.push(def.nextRoute);
    commit(tour, step + 1);
  }, [step, tour, commit, finish, router]);

  const back = useCallback(() => {
    if (step <= 0 || !tour) return;
    const prev = stepsFor(tour)[step - 1];
    if (prev.page && pathRef.current !== prev.page) router.push(prev.page);
    commit(tour, step - 1);
  }, [step, tour, commit, router]);

  // First-visit welcome: auto-pop the tour once, only the very first time the
  // user lands on an RFP pack page. The flag is set straight away so it never
  // re-opens after a Skip or Finish.
  useEffect(() => {
    if (readStored()) return; // an in-flight tour already exists — don't stack
    let seen = false;
    try {
      seen = localStorage.getItem(LS_VISITED) === "1";
    } catch { /* ignore */ }
    if (seen) return;
    try {
      localStorage.setItem(LS_VISITED, "1");
    } catch { /* ignore */ }
    const id = window.setTimeout(() => {
      const p = pathRef.current;
      if (p === DASH) start("create");
      else if (PACK_RE.test(p)) start("canvas");
    }, 900);
    return () => window.clearTimeout(id);
  }, [start]);

  // Escape closes the tour.
  useEffect(() => {
    if (step < 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") finish();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, next, back, finish]);

  const active = tour !== null && step >= 0;
  const api: PackTourApi = {
    active,
    tour: tour ?? "create",
    step: Math.max(step, 0),
    total: stepsFor(tour ?? "create").length,
    start,
    finish,
  };

  return (
    <TourCtx.Provider value={api}>
      {children}
      {active && tour && <TourOverlay tour={tour} step={step} api={api} onNext={next} onBack={back} />}
    </TourCtx.Provider>
  );
}

const STYLES = `
@keyframes pkTourPop {
  0% { transform: translateY(10px) scale(0.96); opacity: 0; }
  100% { transform: translateY(0) scale(1); opacity: 1; }
}
@keyframes pkTourPulse {
  0% { box-shadow: 0 0 0 100vmax rgba(8,12,30,0.6), 0 0 0 2px rgba(91,108,255,0.85), 0 0 20px rgba(91,108,255,0.4); }
  55% { box-shadow: 0 0 0 100vmax rgba(8,12,30,0.6), 0 0 0 2.5px rgba(91,108,255,0.95), 0 0 26px rgba(91,108,255,0.55); }
  100% { box-shadow: 0 0 0 100vmax rgba(8,12,30,0.6), 0 0 0 2px rgba(91,108,255,0.85), 0 0 20px rgba(91,108,255,0.4); }
}
@keyframes pkTourBar {
  from { width: 0%; }
}
@keyframes pkTourRing {
  0% { transform: scale(0.98); opacity: 0.4; }
  70% { transform: scale(1.08); opacity: 0.12; }
  100% { transform: scale(1.12); opacity: 0; }
}
@keyframes pkTourRingDelayed {
  0% { transform: scale(0.98); opacity: 0.4; }
  70% { transform: scale(1.08); opacity: 0.12; }
  100% { transform: scale(1.12); opacity: 0; }
}
@keyframes pkTourGlow {
  0%, 100% { box-shadow: 0 0 0 0 rgba(91,108,255,0); }
  50% { box-shadow: 0 0 14px 2px rgba(91,108,255,0.3); }
}
`;

interface RectLike { x: number; y: number; w: number; h: number; }

function sameRect(a: RectLike | null, b: RectLike | null, tol = 3) {
  return !a || !b ? false : Math.abs(a.x - b.x) <= tol && Math.abs(a.y - b.y) <= tol && Math.abs(a.w - b.w) <= tol && Math.abs(a.h - b.h) <= tol;
}

function TourOverlay({ tour, step, api, onNext, onBack }: { tour: TourId; step: number; api: PackTourApi; onNext: () => void; onBack: () => void }) {
  const list = stepsFor(tour);
  const def = list[step] ?? list[0];
  const [rect, setRect] = useState<RectLike | null>(null);

  // Follow the spotlight target (re-measures while the page scrolls).
  useEffect(() => {
    let stop = false;
    let tries = 0;
    let timer = 0;
    let raf = 0;
    const reset = requestAnimationFrame(() => { if (!stop) setRect(null); });
    if (!def.target) return () => { stop = true; cancelAnimationFrame(reset); };
    const measure = (el: Element) => {
      if (stop) return;
      const r = el.getBoundingClientRect();
      const next: RectLike = { x: r.left - 8, y: r.top - 8, w: r.width + 16, h: r.height + 16 };
      setRect((prev) => (sameRect(prev, next) ? prev : next));
      raf = requestAnimationFrame(() => measure(el));
    };
    const tick = () => {
      if (stop) return;
      const el = document.querySelector(def.target!);
      if (!el) {
        if (++tries < 24) { timer = window.setTimeout(tick, 260); return; }
        setRect(null);
        return;
      }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      timer = window.setTimeout(() => measure(el), 460);
    };
    tick();
    const onResize = () => { /* next rAF re-measures anyway */ };
    window.addEventListener("resize", onResize);
    return () => {
      stop = true;
      cancelAnimationFrame(reset);
      window.clearTimeout(timer);
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [step, tour, def.target]);

  const isLast = step >= list.length - 1;
  const rectBox = rect;

  // Position the card next to the spotlight target.
  let cardTop = 0;
  let cardLeft = 0;
  let arrowLeft: number | null = null;
  const cardW = 380;
  if (rectBox) {
    const pad = 18;
    const below = rectBox.y + rectBox.h + pad;
    if (below + 260 < window.innerHeight) {
      cardTop = below;
      arrowLeft = Math.min(Math.max(rectBox.x + rectBox.w / 2 - 24, 28), cardW - 28);
    } else if (rectBox.y - 260 - pad > 0) {
      cardTop = rectBox.y - 260 - pad;
      arrowLeft = Math.min(Math.max(rectBox.x + rectBox.w / 2 - 24, 28), cardW - 28);
    } else {
      cardTop = window.innerHeight / 2 - 150;
    }
    cardLeft = Math.min(Math.max(16, rectBox.x + rectBox.w / 2 - cardW / 2), Math.max(16, window.innerWidth - cardW - 16));
  } else {
    cardTop = Math.max(16, window.innerHeight / 2 - 170);
    cardLeft = Math.max(16, window.innerWidth / 2 - cardW / 2);
  }

  const pct = Math.round(((step + 1) / list.length) * 100);

  return (
    <>
      <style>{STYLES}</style>
      <div style={{ position: "fixed", inset: 0, zIndex: 2147483600, fontFamily: CARD_FONT }} aria-modal="true" role="dialog" aria-label={def.title}>
        {rectBox ? (
          <div
            style={{
              position: "fixed",
              left: rectBox.x,
              top: rectBox.y,
              width: rectBox.w,
              height: rectBox.h,
              borderRadius: 18,
              boxShadow: "0 0 0 100vmax rgba(8,12,30,0.62), 0 0 0 2px rgba(91,108,255,0.9), 0 0 26px rgba(91,108,255,0.55)",
              animation: "pkTourPulse 3.2s ease-in-out infinite",
              pointerEvents: "none",
              zIndex: 1,
              transition: "left 240ms ease, top 240ms ease, width 240ms ease, height 240ms ease",
            }}
          />
        ) : (
          <div style={{ position: "absolute", inset: 0, background: "radial-gradient(1200px 700px at 50% 30%, rgba(8,12,30,0.55) 0%, rgba(8,12,30,0.72) 100%)", zIndex: 1, pointerEvents: "none" }} />
        )}

        {/* Step card */}
        <div
          style={{
            position: "fixed",
            left: cardLeft,
            top: cardTop,
            width: cardW,
            maxWidth: "92vw",
            zIndex: 3,
            background: "linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%)",
            color: "#172554",
            borderRadius: 24,
            border: "1px solid rgba(91,108,255,0.18)",
            boxShadow: "0 30px 70px rgba(6,10,25,0.42), 0 8px 26px rgba(6,10,25,0.22)",
            overflow: "visible",
            animation: "pkTourPop 260ms ease-out",
          }}
        >
          {/* Arrow pointing at the target */}
          {arrowLeft !== null && (
            <div
              style={{
                position: "absolute",
                top: cardTop < rectBox!.y ? undefined : -7,
                bottom: cardTop < rectBox!.y ? -7 : undefined,
                left: arrowLeft,
                width: 14,
                height: 14,
                transform: "rotate(45deg)",
                background: "linear-gradient(180deg, #ffffff 0%, #f6f9ff 100%)",
                borderLeft: cardTop < rectBox!.y ? "1px solid rgba(91,108,255,0.18)" : undefined,
                borderTop: cardTop < rectBox!.y ? "1px solid rgba(91,108,255,0.18)" : undefined,
                borderRight: cardTop < rectBox!.y ? undefined : "1px solid rgba(91,108,255,0.18)",
                borderBottom: cardTop < rectBox!.y ? undefined : "1px solid rgba(91,108,255,0.18)",
              }}
            />
          )}

          {/* Gradient header band */}
          <div style={{ height: 6, background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT2})` }} />

          <div style={{ padding: "18px 22px 16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 13,
                  background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT2})`,
                  color: "#fff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 800,
                  fontSize: 15,
                  boxShadow: "0 6px 16px rgba(91,108,255,0.4)",
                  flex: "none",
                }}
              >
                {step + 1}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.16em", textTransform: "uppercase", color: ACCENT, marginBottom: 2 }}>
                  {tour === "canvas" ? "Pack tour" : "RFP pack tour"} · step {step + 1} of {list.length}
                </div>
                <div style={{ height: 5, borderRadius: 99, background: "#e8ecf9", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${pct}%`, borderRadius: 99, background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT2})`, transition: "width 260ms ease", animation: "pkTourBar 320ms ease" }} />
                </div>
              </div>
            </div>

            <div style={{ fontSize: 18, fontWeight: 800, letterSpacing: "-0.01em", marginBottom: 6 }}>{def.title}</div>
            <div style={{ fontSize: 13.5, lineHeight: 1.58, color: "#3e4f6b", marginBottom: 16 }}>{def.body}</div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                onClick={onBack}
                disabled={step === 0}
                style={{
                  border: "1px solid #d8e0f2",
                  background: "#ffffff",
                  color: "#3b4771",
                  borderRadius: 12,
                  padding: "9px 14px",
                  fontSize: 13,
                  fontWeight: 650,
                  cursor: step === 0 ? "default" : "pointer",
                  opacity: step === 0 ? 0.45 : 1,
                  boxShadow: "0 1px 2px rgba(6,10,25,0.06)",
                }}
              >
                Back
              </button>

              <button
                onClick={api.finish}
                title="End the tour now"
                style={{
                  border: "none",
                  background: "transparent",
                  color: "#8b96b8",
                  fontSize: 12.5,
                  fontWeight: 700,
                  letterSpacing: "0.02em",
                  cursor: "pointer",
                  padding: "9px 6px",
                }}
              >
                Skip tour
              </button>

              <div style={{ flex: 1 }} />

              <button
                onClick={onNext}
                style={{
                  border: "none",
                  borderRadius: 12,
                  padding: "10px 22px",
                  fontSize: 13,
                  fontWeight: 750,
                  color: "#fff",
                  cursor: "pointer",
                  boxShadow: `0 8px 20px rgba(91,108,255,0.45)`,
                  background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT2})`,
                }}
              >
                {isLast ? "Finish tour" : "Next"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export function TourStartButton({ label = "Take the tour", block = false }: { label?: string; block?: boolean }) {
  const api = usePackTour();
  if (!api) return null;
  return (
    <>
      <style>{STYLES}</style>
      <span style={{ position: "relative", display: block ? "block" : "inline-block", flex: block ? "1" : undefined }}>
        <span aria-hidden style={{ position: "absolute", inset: -4, borderRadius: 999, border: `2px solid ${ACCENT2}`, pointerEvents: "none", animation: "pkTourRing 2.6s ease-out infinite", opacity: 0 }} />
        <span aria-hidden style={{ position: "absolute", inset: -4, borderRadius: 999, border: `2px solid ${ACCENT}`, pointerEvents: "none", animation: "pkTourRingDelayed 2.6s ease-out infinite 1.3s", opacity: 0 }} />
        <button
          onClick={() => api.start()}
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            width: block ? "100%" : undefined,
            border: "1.5px solid rgba(91,108,255,0.55)",
            background: "linear-gradient(135deg, rgba(91,108,255,0.14), rgba(236,72,153,0.1))",
            color: ACCENT,
            borderRadius: 999,
            padding: "8px 16px",
            fontSize: 13,
            fontWeight: 750,
            cursor: "pointer",
            animation: "pkTourGlow 2.8s ease-in-out infinite",
          }}
        >
          <span style={{ display: "inline-flex", width: 18, height: 18, borderRadius: "50%", alignItems: "center", justifyContent: "center", background: `linear-gradient(135deg, ${ACCENT}, ${ACCENT2})`, color: "#fff", fontSize: 8, fontWeight: 800 }}>
            ▶
          </span>
          {label}
        </button>
      </span>
    </>
  );
}